import { theme } from '../theme'

/**
 * THE batch container-status badge and progress bar, for every page that lists batches.
 *
 * WHY THIS MODULE EXISTS
 *
 * `design.md` §8.2 asked for it so "a badge colour cannot diverge between the two pages", and
 * until now it had not shipped: `TacticalE2E.tsx` defined both components in-file and
 * unexported, and `InternalE2E.tsx` — the page that exists to be compared AGAINST the public
 * one — rendered the raw `{b.status}` string. The same batch state therefore appeared as a
 * styled badge on one page and a bare lowercase word on the other.
 *
 * That is more than a cosmetic split, because this vocabulary MOVES. The lifecycle migration
 * retired `sent_pending_reception` for `inbound_processing` on the transmission side, and twelve
 * attribution tests pinned the retired literal unnoticed for weeks (B2.19). A vocabulary that
 * changes, rendered by two independent pieces of code, is precisely the setup where one surface
 * quietly stops recognising a value.
 *
 * SO AN UNRECOGNISED STATUS IS RENDERED VISIBLY UNRECOGNISED, never blank and never dressed as
 * a state we know. `COLOURS` below is the whole of what this surface claims to understand;
 * anything else gets the dashed magenta treatment and says so in its tooltip. A blank cell is
 * the failure this module was written to remove — it is indistinguishable from "nothing is
 * happening", which is the one thing a status column must never imply by accident.
 *
 * `COLOURS`' keys are checked against `backend/models/batch.py`'s `BATCH_CONTAINER_STATUSES`
 * by `tests/test_batch_status_is_shared.py`, so a status added to the engine fails the build
 * here rather than appearing as an unrecognised badge in production.
 */

/** The container statuses this surface has a treatment for. Mirrors `BATCH_CONTAINER_STATUSES`. */
export const COLOURS: Record<string, string> = {
  // The lifecycle, in the order it runs.
  dispatching: '#ffa726',
  in_flight: '#42a5f5',
  complete: '#4caf50',
  // Off that line: a batch reported on while items are still open.
  partial: '#ab47bc',
}

/**
 * The treatment for a value this surface does not know.
 *
 * Magenta appears nowhere in `COLOURS`, and the border is DASHED rather than solid, so the
 * signal does not depend on telling two colours apart. The previous in-page badge gave every
 * unknown value the same neutral grey it gave a recognised one, which made "we do not know what
 * this is" look like an ordinary state.
 */
const UNRECOGNISED = '#e040fb'

/** What a blank or absent status prints. Never the empty string: an empty cell says nothing. */
const BLANK_LABEL = '(blank)'

export function StatusBadge({ status }: { status: string | null | undefined }) {
  const raw = (status ?? '').trim()
  const known = Object.prototype.hasOwnProperty.call(COLOURS, raw)
  const colour = known ? COLOURS[raw] : UNRECOGNISED
  return (
    <span
      title={
        known
          ? undefined
          : `This page does not recognise the batch status ${raw ? `"${raw}"` : '(blank)'}. ` +
            'It is shown as the server sent it. The badge vocabulary is declared in ' +
            'backend/models/batch.py.'
      }
      style={{
        background: colour + '22',
        color: colour,
        border: `1px ${known ? 'solid' : 'dashed'} ${colour}${known ? '55' : 'cc'}`,
        borderRadius: 3,
        padding: '0.1rem 0.45rem',
        fontSize: '0.72rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {known ? raw : `⚠ ${raw || BLANK_LABEL}`}
    </span>
  )
}

/**
 * The counts `Progress` needs, and nothing else.
 *
 * Declared structurally rather than as one page's `Batch` type so both pages' own row types
 * satisfy it without either importing the other's. All four fields are on every `/api/batches`
 * summary — `BatchResponse` publishes `queued`/`in_flight`/`terminal` for every batch, public
 * and internal alike.
 */
export type BatchProgressCounts = {
  size: number
  queued: number
  in_flight: number
  terminal: number
}

export function Progress({ b }: { b: BatchProgressCounts }) {
  const total = b.size || 1
  const seg = (n: number, colour: string, label: string) =>
    n > 0 ? (
      <span
        title={`${n} ${label}`}
        style={{ width: `${(n / total) * 100}%`, background: colour, display: 'block', height: '100%' }}
      />
    ) : null
  return (
    <div>
      <div style={{
        display: 'flex', height: 8, borderRadius: 3, overflow: 'hidden',
        background: theme.bgElevated, minWidth: '7rem',
      }}>
        {seg(b.terminal, '#4caf50', 'finished')}
        {seg(b.in_flight, '#42a5f5', 'in flight')}
        {seg(b.queued, '#555', 'queued')}
      </div>
      <div style={{ fontSize: '0.68rem', color: '#9aa0a6', marginTop: '0.2rem' }}>
        {b.terminal} done · {b.in_flight} in flight · {b.queued} queued
      </div>
    </div>
  )
}
